import "url-search-params-polyfill";
import { ApplicationScope } from "@superblocksteam/shared";
import { produce } from "immer";
import { createSelector } from "reselect";
import { accessibleEntityProps } from "autocomplete/dataTreeTypeDefCreator";
import { DataTreeFactory } from "legacy/entities/DataTree/dataTreeFactory";
import {
  getEmbedPropsAndMeta,
  getV2ApiAppInfo,
  getWidgetsMeta,
} from "legacy/selectors/sagaSelectors";
import { selectAiWidgetChangesById } from "store/slices/ai/selectors";
import { selectUserAccessibleScopeForApiUnion } from "store/slices/apisShared/selectors/selectUserAccessibleScopeForApiUnion";
import {
  selectAllV2Apis,
  selectV2ApiMeta,
} from "store/slices/apisV2/selectors";
import { getAllEvents } from "store/slices/application/events/selectors";
import { getStateVarsAndMeta } from "store/slices/application/stateVarsMeta/selectors";
import { getAllTimers } from "store/slices/application/timers/selectors";
import { getAllTimersMeta } from "store/slices/application/timersMeta/selectors";
import { selectFlags } from "store/slices/featureFlags";
import { selectOnlyOrganization } from "../../store/slices/organizations";
import { getMergedDataTree as getMergedDataTreeFunction } from "../../utils/dataTree/MergedDataTree";
import { resolveScopes } from "../../utils/dataTree/resolveScopes";
import { getAppData, getWidgets } from "./entitiesSelector";
import { createMarkedSelector } from "./markedSelector";
import { getUserAvailableTheme } from "./themeSelectors";
import type {
  DataTree,
  DataTreeAction,
} from "legacy/entities/DataTree/dataTreeFactory";
import type { AppState } from "store/types";
import type { ApiScope } from "utils/dataTree/scope";

const getAvailableIcons = (): Record<string, string> => ({});
const getDynamicLayoutWidgets = (_state: AppState): Record<string, any> => ({});

export const getEvaluationInverseDependencyMap = (state: AppState) =>
  state.legacy.evaluations.dependencies.inverseDependencyMap;

export const getNameIteratorValue = (state: AppState) =>
  state.legacy.evaluations.nameIterator;

const getEvaluationType = (state: AppState) =>
  state.legacy.evaluations.evaluationType;

export const getUnevaluatedDataTree = createMarkedSelector(
  "getUnevaluatedDataTree",
)(
  getEvaluationType,
  selectAllV2Apis,
  selectV2ApiMeta,
  getV2ApiAppInfo,
  getWidgets,
  getWidgetsMeta,
  getDynamicLayoutWidgets,
  getAllTimers,
  getAllTimersMeta,
  getStateVarsAndMeta,
  (state: AppState) => state.legacy.entities.pageList.pages,
  getAppData,
  selectOnlyOrganization,
  getUserAvailableTheme,
  selectFlags,
  getEmbedPropsAndMeta,
  getAllEvents,
  selectAiWidgetChangesById,
  (
    evaluationType,
    apiV2Entities,
    apiV2Outputs,
    apiV2AppInfo,
    widgets,
    widgetsMeta,
    widgetsDynamics,
    timers,
    timersMeta,
    stateVarsAndMeta,
    pageListPayload,
    appData,
    orgData,
    themeData,
    featureFlags,
    embedPropsAndMeta,
    eventMap,
    aiEdits,
  ) => {
    const iconData = getAvailableIcons();
    const pageList = pageListPayload || [];
    const unevalData =
      evaluationType === "ui"
        ? DataTreeFactory.create({
            apiEntities: apiV2Entities,
            apiOutputs: apiV2Outputs,
            apiAppInfo: apiV2AppInfo,
            widgets,
            widgetsMeta,
            widgetsDynamics,
            timers,
            timersMeta,
            stateVarsAndMeta,
            embedPropsAndMeta,
            pageList,
            appData,
            orgData,
            themeData,
            iconData,
            featureFlags,
            eventMap,
            aiEdits,
          })
        : DataTreeFactory.create({
            apiEntities: apiV2Entities,
            apiOutputs: apiV2Outputs,
            apiAppInfo: {},
            widgets: {},
            widgetsMeta: {},
            widgetsDynamics: {},
            timers: {},
            timersMeta: {
              [ApplicationScope.GLOBAL]: {},
              [ApplicationScope.APP]: {},
              [ApplicationScope.PAGE]: {},
            },
            stateVarsAndMeta: {},
            embedPropsAndMeta: undefined,
            pageList: [],
            appData,
            orgData,
            themeData,
            iconData: [],
            featureFlags,
            eventMap: {},
          });

    return unevalData;
  },
);

/**
 * returns evaluation tree object
 *
 * @param state
 */
export const getDataTree = (state: AppState) => state.legacy.evaluations.tree;

// For autocomplete. Use actions cached responses if
// there isn't a response already
export const getDataTreeForAutocomplete = createSelector(
  getDataTree,
  getEvaluationType,
  (tree, evaluationType) => {
    if (evaluationType === "headless") {
      return produce(tree, (draft) => {
        delete draft.GLOBAL.Global;
        delete draft.GLOBAL.theme;
        delete draft.GLOBAL.icons;
      });
    }
    return tree;
  },
);

export const getMergedDataTree = createSelector(
  getDataTreeForAutocomplete,
  (_state: unknown, scope: ApplicationScope) => scope,
  (dataTree, scope) => getMergedDataTreeFunction(scope, dataTree),
);

function applyApiScope(
  apiScope: ApiScope,
  tree: DataTree,
  mergedDataTree: Record<string, unknown>,
) {
  for (const actionName of apiScope.previousV1ActionNames) {
    const entity = tree.PAGE[apiScope.apiName] as DataTreeAction | undefined;
    const { output } = entity?.actionOutputs[actionName] ?? {
      output: null,
    };
    mergedDataTree[actionName] = { output };
  }
  for (const inScopeVar of Object.keys(apiScope.v2ComputedScope ?? {})) {
    mergedDataTree[inScopeVar] = apiScope.v2ComputedScope?.[inScopeVar];
  }
}

export const getUserAccessibleDataTree = createSelector(
  getDataTreeForAutocomplete, // Get App data
  selectUserAccessibleScopeForApiUnion, // Get API data
  (
    _state: unknown,
    _apiId: unknown,
    _actionId: unknown,
    appScope?: ApplicationScope,
  ) => appScope ?? ApplicationScope.PAGE,
  (tree, apiScope, appScope) => {
    const scopes = resolveScopes(appScope);
    const mergedDataTree = {} as Record<string, unknown>;
    for (const { scope, prefix } of scopes) {
      const focusedScope = tree[scope];
      if (prefix) {
        const prefixedDataTree: Record<string, unknown> = {};
        for (const [key, entity] of Object.entries(focusedScope)) {
          prefixedDataTree[key] = accessibleEntityProps(entity);
        }
        mergedDataTree[prefix] = prefixedDataTree;
      } else {
        for (const [key, entity] of Object.entries(focusedScope)) {
          mergedDataTree[key] = accessibleEntityProps(entity);
        }
      }
    }

    // APPLY the API scope
    if (apiScope) {
      applyApiScope(apiScope, tree, mergedDataTree);
    }

    return mergedDataTree;
  },
);

export const getMergedDataTreeKeys = createSelector(
  [getMergedDataTree],
  (dataTree): string[] => Object.keys(dataTree),
);

export const getEmbedEntityNames = createSelector(
  getDataTree,
  (dataTree): string[] => {
    return Object.keys(dataTree.PAGE.Embed ?? {});
  },
);
